import * as React from "react";
import { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { underControl } from "../redux/userRelated/userSlice";
import { underStudentControl } from "../redux/studentRelated/studentSlice";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloseIcon from "@mui/icons-material/Close";
import { getAppDialogProps } from "../utils/dialogLayer";

/** How long centered dialogs auto-dismiss (success/warning only). */
export const SUCCESS_POPUP_AUTO_HIDE_MS = 3200;

export function inferPopupSeverity(message) {
    if (message == null || message === "") return "error";
    const text = String(message).trim();
    if (
        text === "Done Successfully" ||
        /successfully/i.test(text) ||
        /student created/i.test(text) ||
        /^created \d+ class/i.test(text) ||
        /has been added/i.test(text) ||
        /login details copied/i.test(text) ||
        /successfully created \d+ fee structure/i.test(text) ||
        /fee structure saved successfully/i.test(text) ||
        /draft saved/i.test(text) ||
        /attendance draft saved/i.test(text) ||
        /attendance sent/i.test(text) ||
        /attendance saved/i.test(text) ||
        /saved attendance/i.test(text) ||
        /sent to admin/i.test(text) ||
        /saved successfully/i.test(text) ||
        /changes saved/i.test(text) ||
        text === "Changes saved" ||
        /completed successfully/i.test(text) ||
        /page is now live/i.test(text) ||
        /scheme approved/i.test(text) ||
        /uploaded successfully/i.test(text) ||
        /was submitted/i.test(text) ||
        /profile saved/i.test(text) ||
        /proceed to the/i.test(text) ||
        /profile setup complete/i.test(text) ||
        /resource deleted/i.test(text) ||
        /role updated/i.test(text) ||
        /role set to/i.test(text) ||
        /is now form teacher/i.test(text) ||
        /is now class teacher/i.test(text) ||
        /is now .* of/i.test(text) ||
        /can sign in/i.test(text) ||
        /portal access is off/i.test(text) ||
        /portal access has been turned off/i.test(text) ||
        /enrollment verified/i.test(text) ||
        /enrollment rejected/i.test(text) ||
        /marks accepted/i.test(text) ||
        /subject marks accepted/i.test(text) ||
        /marks sent/i.test(text) ||
        /sent to your form teacher/i.test(text) ||
        /sent to the director/i.test(text) ||
        /class results sent/i.test(text) ||
        /sent to director/i.test(text) ||
        /appear under submitted/i.test(text) ||
        /record another test/i.test(text) ||
        /\d+\s+students?\s+processed/i.test(text) ||
        /import complete/i.test(text) ||
        /password updated successfully/i.test(text) ||
        /payment verified/i.test(text) ||
        /payment approved and recorded/i.test(text)
    ) {
        return "success";
    }
    if (/import finished with errors/i.test(text)) {
        return "warning";
    }
    if (
        /no active fee structure|fee records already|already exist|were skipped|notice:/i.test(
            text
        ) ||
        /all \d+ class\(es\) already have a fee structure/i.test(text) ||
        /fee structures already exist/i.test(text) ||
        /enter at least one|enter marks for at least/i.test(text) ||
        /payment submission rejected/i.test(text)
    ) {
        return "warning";
    }
    return "error";
}

const SEVERITY_CONFIG = {
    success: {
        Icon: CheckCircleOutlineIcon,
        iconColor: "success.main",
        title: "Well done!",
        buttonColor: "success",
    },
    warning: {
        Icon: WarningAmberOutlinedIcon,
        iconColor: "warning.main",
        title: "Please check",
        buttonColor: "warning",
    },
    error: {
        Icon: ErrorOutlineIcon,
        iconColor: "error.main",
        title: "Could not complete",
        buttonColor: "error",
    },
};

const Popup = ({ message, setShowPopup, showPopup, severity: severityProp }) => {
    const dispatch = useDispatch();
    const severity = severityProp || inferPopupSeverity(message);
    const config = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.error;
    const { Icon, iconColor, title, buttonColor } = config;
    const autoDismiss = severity === "success" || severity === "warning";

    const handleClose = useCallback(() => {
        setShowPopup(false);
        dispatch(underControl());
        dispatch(underStudentControl());
    }, [dispatch, setShowPopup]);

    useEffect(() => {
        if (!showPopup || !autoDismiss) return undefined;
        const timer = setTimeout(handleClose, SUCCESS_POPUP_AUTO_HIDE_MS);
        return () => clearTimeout(timer);
    }, [showPopup, autoDismiss, message, handleClose]);

    if (!showPopup || !message) return null;

    const dialogLayer = getAppDialogProps({
        paperSx: {
            borderRadius: 3,
            textAlign: "center",
            mx: 2,
            boxShadow: (t) => t.shadows[24],
        },
    });

    return (
        <Dialog
            open={showPopup}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            aria-labelledby="popup-feedback-title"
            {...dialogLayer}
        >
            <IconButton
                aria-label="Close"
                onClick={handleClose}
                sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: "text.secondary",
                    zIndex: 1,
                    "&:hover": {
                        bgcolor: (t) => t.palette.action.hover,
                    },
                }}
            >
                <CloseIcon />
            </IconButton>

            <DialogContent sx={{ pt: 4, pb: 2, px: 3 }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1.5,
                    }}
                >
                    <Icon sx={{ fontSize: 72, color: iconColor }} aria-hidden />
                    <Typography
                        id="popup-feedback-title"
                        variant="h5"
                        component="h2"
                        fontWeight={700}
                    >
                        {title}
                    </Typography>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ maxWidth: 360, lineHeight: 1.5, whiteSpace: "pre-line" }}
                    >
                        {message}
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", pb: 3, pt: 0 }}>
                <Button
                    variant="contained"
                    color={buttonColor}
                    size="large"
                    onClick={handleClose}
                    sx={{ minWidth: 140, borderRadius: 2, fontWeight: 600 }}
                >
                    OK
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default Popup;
